/*!
 * Hurdler: https://github.com/jaydenseric/Hurdler
 * @version v4.0.1
 * @author Jayden Seric
 * @license MIT
 */

/**
 * Creates a new Hurdler session. There should only be one instance per window.
 * @class
 * @param {String} [$0.hashPrefix='/'] - String between URL hash symbol and target element ID to denote Hurdler links. Prevents auto scroll.
 * @param {function} [$0.onSprintBefore] - Runs before each sprint.
 * @param {function} [$0.onSprintAfter] - Runs after each sprint.
 * @param {Hurdle[]} [$0.hurdles=[]] - List of hurdles.
 * @example
 * const hurdler = new Hurdler({
 *   hashPrefix: '!/'
 * })
 */
function Hurdler() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$hashPrefix = _ref.hashPrefix,
      hashPrefix = _ref$hashPrefix === undefined ? '/' : _ref$hashPrefix,
      onSprintBefore = _ref.onSprintBefore,
      onSprintAfter = _ref.onSprintAfter,
      _ref$hurdles = _ref.hurdles,
      hurdles = _ref$hurdles === undefined ? [] : _ref$hurdles;

  this.hashPrefix = hashPrefix;
  this.onSprintBefore = onSprintBefore;
  this.onSprintAfter = onSprintAfter;
  this.hurdles = hurdles;

  /**
   * The current URL hash target element, or null if nonexistent.
   * @type {HTMLElement|null}
   * @example
   * console.log(hurdler.target)
   */
  this.target = null;

  // Sprint every URL hash change
  window.addEventListener('hashchange', this.sprint.bind(this));
}

/**
 * Adds a hurdle.
 * @param {Hurdle} options - A hurdle.
 * @example
 * hurdler.addHurdle({
 *   test: element => {
 *     return element.tagName === 'SECTION'
 *   },
 *   onJump: element => {
 *     console.log('Jumped a section', element)
 *   }
 * })
 */
Hurdler.prototype.addHurdle = function (options) {
  this.hurdles.push(options);
};

/**
 * Checks a provided URL hash matches the configured format.
 * @param {string} hash - A URL hash to validate.
 * @returns {boolean} URL hash validity.
 * @example
 * hurdler.validateHash(anExampleLinkElement.hash)
 */
Hurdler.prototype.validateHash = function (hash) {
  // http://stackoverflow.com/a/4579228/1596978
  return hash.lastIndexOf(this.hashPrefix, 0) === 0;
};

/**
 * Gets the URL hash target element.
 * @private
 * @returns {HTMLElement|null} Element with the ID targeted, or null if nonexistent.
 */
Hurdler.prototype.getTarget = function () {
  var hash = window.location.hash.split('#' + this.hashPrefix);
  return hash.length === 2 ? document.getElementById(hash[1]) : null;
};

/**
 * Sets the URL hash target element, triggering a sprint.
 * @param {HTMLElement} element - Element with an ID to target.
 * @example
 * hurdler.setTarget(anExampleElement)
 */
Hurdler.prototype.setTarget = function (element) {
  window.location.hash = this.hashPrefix + element.id;
};

/**
 * Clears the window URL hash if a given element is targeted, or if the URL hash matches the configured Hurdler format.
 * @param {HTMLElement} [element] - Element with an ID that you do not want targeted.
 * @example
 * hurdler.clearTarget(anExampleElement)
 */
Hurdler.prototype.clearTarget = function (element) {
  if (element === undefined && this.validateHash(window.location.hash) || window.location.hash === '#' + this.hashPrefix + element.id) this.constructor.clearHash();
};

/**
 * Jumps hurdles and runs callbacks for the current URL hash. Use after all hurdles have been added and the document is ready. Triggered automatically by URL hash changes.
 * @example
 * hurdler.sprint()
 */
Hurdler.prototype.sprint = function () {
  var _this = this;

  // Update the target DOM element
  this.target = this.getTarget();
  // Only sprint if there is a target to start at
  if (this.target) {
    (function () {
      // Run before sprint callback
      if (typeof _this.onSprintBefore === 'function') _this.onSprintBefore();
      // Search from the target up the DOM for hurdles
      var jumps = [];
      var testElement = _this.target;
      do {
        // Test every hurdle against the element
        _this.hurdles.forEach(function (hurdle) {
          if (hurdle.test(testElement)) {
            // Store hurdle jump for callbacks later
            jumps.unshift({
              element: testElement,
              hurdle: hurdle
            });
          }
        });
      } while (testElement = testElement.parentElement);
      // Run hurdle jump callbacks
      jumps.forEach(function (jump) {
        if (typeof jump.hurdle.onJump === 'function') jump.hurdle.onJump(jump.element);
      });
      // Run after sprint callback
      if (typeof _this.onSprintAfter === 'function') _this.onSprintAfter();
    })();
  }
};

/**
 * Clears the URL hash.
 * @private
 */
Hurdler.clearHash = function () {
  window.history.pushState('', document.title, window.location.pathname + window.location.search);
};

export default Hurdler;
//# sourceMappingURL=hurdler.module.js.map



//////////////////
// WEBPACK FOOTER
// ./~/hurdler/dist/hurdler.module.js
// module id = 14
// module chunks = 0